import { useState, useRef, useEffect } from 'react';
import {
  Users,
  MessageCircle,
  TrendingUp,
  Shield,
  X,
  Rocket,
  ArrowRight,
  Star,
  CheckCircle,
  Zap,
} from 'lucide-react';

const BENEFITS = [
  {
    icon: Users,
    title: 'Build Connections',
    desc: 'Foster meaningful relationships and networking opportunities within your community.',
    gradient: 'from-purple-500 to-indigo-600',
    stat: '↑ 300%',
  },
  {
    icon: MessageCircle,
    title: 'Engage Members',
    desc: 'Keep your community active with discussions, events, and interactive content.',
    gradient: 'from-blue-500 to-purple-600',
    stat: '85% active',
  },
  {
    icon: TrendingUp,
    title: 'Scale Growth',
    desc: 'Grow your community organically with powerful tools and analytics insights.',
    gradient: 'from-green-500 to-blue-600',
    stat: '10× faster',
  },
  {
    icon: Shield,
    title: 'Stay Secure',
    desc: 'Maintain a safe environment with advanced moderation and privacy controls.',
    gradient: 'from-red-500 to-purple-600',
    stat: '99.9% secure',
  },
];

function DemoModal({ open, onClose }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const first = dialogRef.current?.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    first?.focus();
    const handleKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl animate-in fade-in zoom-in-95"
      >
        <header className="flex items-center justify-between dynamic-site-bg px-6 py-4 text-white">
          <h2 className="flex items-center gap-3 text-lg font-semibold">
            <Rocket className="h-5 w-5" />
            Start Your Community Journey
          </h2>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="p-8">
          <form className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input type="text" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input type="email" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Community Focus</label>
              <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600">
                <option>Professional Networking</option>
                <option>Hobby & Interests</option>
                <option>Learning & Education</option>
                <option>Local Community</option>
                <option>Business & Entrepreneurship</option>
              </select>
            </div>
            <button type="submit" className="w-full dynamic-site-bg text-white py-3 rounded-lg font-semibold">
              Get Started Free
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function Benefits() {
  const [openModal, setOpenModal] = useState(false);

  return (
    <>
      <section id="whyChoose" className="bg-gradient-to-b from-white to-gray-50 pt-20 pb-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

          <header className="mx-auto mb-16 max-w-4xl text-center">
            <span className="mb-6 inline-flex items-center gap-2 rounded-full bg-purple-600/10 px-4 py-2 text-sm font-medium text-purple-600">
              <Star className="h-4 w-4" />
              Why choose Communiticate?
            </span>
            <h2 className="mb-6 text-3xl font-extrabold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
              Transform Your Vision Into{' '}
              <span className="block  bg-gradient-to-r leading-normal from-purple-600 to-blue-600 bg-clip-text text-transparent">
                Thriving Communities
              </span>
            </h2>
            <p className="mx-auto text-xl leading-relaxed text-gray-600">
              Communiticate empowers you to build, manage, and grow meaningful communities 
              that bring people together around shared interests and goals.
            </p>

            <p className="mt-6 rounded-2xl border border-purple-600/20 bg-gradient-to-r from-purple-900/5 to-blue-900/5 p-6 text-lg text-gray-700">
              Every great movement starts with a community. With{' '}
              <span className="font-semibold dynamic-site-bg bg-clip-text text-transparent">
                Communiticate
              </span>{' '}
              you can create lasting connections, drive engagement, and make real impact.
            </p>
          </header>

          <ul className="mb-16 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map(({ icon: Icon, title, desc, gradient, stat }) => (
              <li
                key={title}
                className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white px-4 pt-4 shadow-lg transition-all hover:-translate-y-2 hover:border-purple-600/30 hover:shadow-2xl"
              >
                <div className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-transparent via-transparent to-gray-50 opacity-0 transition-opacity group-hover:opacity-100" />
                <div className="text-center">
                  <div
                    className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-r ${gradient} text-white shadow-lg transition-transform group-hover:scale-110`}
                  >
                    <Icon className="h-10 w-10" />
                  </div>

                  <span className="mb-4 inline-flex items-center gap-1 rounded-full bg-purple-600/10 px-3 py-1 text-xs font-medium text-purple-600">
                    <Zap className="h-3 w-3" />
                    {stat}
                  </span>

                  <h3 className="mb-4 text-xl font-semibold text-gray-900 transition-colors group-hover:text-purple-600">
                    {title}
                  </h3>
                  <p className="text-gray-600">{desc}</p>

                  <ArrowRight className="mx-auto mt-4 h-5 w-5 text-purple-600 opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              </li>
            ))}
          </ul>

          <div className="relative overflow-hidden rounded-3xl dynamic-site-bg p-8 text-center sm:p-12">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute right-4 top-4 h-32 w-32 rounded-full bg-white blur-2xl" />
              <div className="absolute bottom-4 left-4 h-24 w-24 rounded-full bg-purple-400 blur-xl" />
            </div>
            <div className="relative mx-auto max-w-3xl text-white">
              <h3 className="mb-4 text-2xl font-bold sm:text-3xl">Ready to Build Your Community?</h3>
              <p className="mb-8 text-xl/[1.4] text-white/90">
                Join thousands of community builders who trust Communiticate to bring people together.
              </p>

              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <button
                  onClick={() => setOpenModal(true)}
                  className="group inline-flex items-center gap-2 rounded-xl bg-white dynamic-site-text-1 px-8 py-4 font-semibold transition-all hover:scale-105 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <Rocket className="h-5 w-5" /> Start Building Today
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>

               
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="mt-16">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600/10 to-blue-600/10 px-4 py-2 rounded-full mb-4">
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-pulse"></div>
                <p className="text-lg font-medium text-gray-700">Trusted by communities worldwide</p>
              </div>
              <div className="w-24 h-1 dynamic-site-bg rounded-full mx-auto"></div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/5 via-blue-600/5 to-purple-600/5 rounded-3xl"></div>
              
              <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto p-8">
                {[
                  { number: '1000+', label: 'Communities', icon: '🏘️', description: 'Active & growing' },
                  { number: '50K+', label: 'Members', icon: '👥', description: 'Connected daily' },
                  { number: '98%', label: 'Satisfaction', icon: '⭐', description: 'Love our platform' },
                ].map(({ number, label, icon, description }, index) => (
                  <div 
                    key={label} 
                    className="group relative text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 hover:border-purple-600/30 transition-all duration-300 hover:-translate-y-2"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 to-blue-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    <div className="relative z-10">
                      <div className="text-3xl mb-3 transform group-hover:scale-110 transition-transform duration-300">
                        {icon}
                      </div>
                      
                      <div className="mb-2">
                        <span className="text-4xl md:text-5xl font-extrabold dynamic-site-bg bg-clip-text text-transparent">
                          {number}
                        </span>
                      </div>
                      
                      <h3 className="text-lg font-semibold text-gray-800 mb-1 group-hover:text-purple-600 transition-colors duration-300">
                        {label}
                      </h3>
                      
                      <p className="text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-300">
                        {description}
                      </p>
                      
                      <div className="mt-4 w-0 h-0.5 dynamic-site-bg rounded-full mx-auto group-hover:w-12 transition-all duration-300"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mt-12 flex flex-wrap justify-center items-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span>Enterprise Ready</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span>GDPR Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span>99.9% Uptime</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span>24/7 Support</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      <DemoModal open={openModal} onClose={() => setOpenModal(false)} />
    </>
  );
}
